import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence, validateFormat } from 'ember-changeset-validations/validators';
import { type SummaryAttributeChangesets } from 'garaje/changesets/summary-attribute';
import PageEditor from 'garaje/components/page-editor';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type GlobalSummaryPage from 'garaje/models/global-summary-page';
import type SummaryPage from 'garaje/models/summary-page';
import type GlobalSettingBatchService from 'garaje/services/global-setting-batch';
import type { ModelData } from 'garaje/utils/type-utils';
import validateConditional from 'garaje/validators/conditional';
import { alias } from 'macro-decorators';

interface FlowsSectionsFinalScreenComponentSignature {
  Args: {
    changeset?: DetailedChangeset<SummaryPage>;
    defaultLocale: string;
    flow: Flow | GlobalFlow;
    isDisabled?: boolean;
    isEditingCustomFinalMessage?: boolean;
    summaryAttributeChangesets?: SummaryAttributeChangesets;
    summaryPage: SummaryPage | GlobalSummaryPage;
    translationEnabledLocales: string[];
    translationLanguagesAvailable: boolean;
  };
}

function buildValidations() {
  return {
    videoUrl: validateConditional({
      if(_key, _newValue, _oldValue, changes, model) {
        return ((<ModelData<SummaryPage>>changes).kind || (<SummaryPage>model).kind) === 'video';
      },
      then: validateFormat({
        type: 'url',
        allowBlank: false,
      }),
      else() {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
    message: validateConditional({
      if(_key, _newValue, _oldValue, changes, model) {
        return ((<ModelData<SummaryPage>>changes).kind || (<SummaryPage>model).kind) === 'message';
      },
      then: validatePresence(true),
      else() {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
    image: validateConditional({
      if(_key, _newValue, _oldValue, changes, model) {
        return ((<ModelData<SummaryPage>>changes).kind || (<SummaryPage>model).kind) === 'image';
      },
      then: validatePresence(true),
      else() {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
  };
}

export default class FlowsSectionsFinalScreen extends Component<FlowsSectionsFinalScreenComponentSignature> {
  @service declare globalSettingBatch: GlobalSettingBatchService;

  @alias('globalSettingBatch.currentGlobalSettingBatch')
  currentGlobalSettingBatch!: GlobalSettingBatchService['currentGlobalSettingBatch'];

  @tracked summaryPage;
  @tracked changeset;
  @tracked summaryAttributeChangesets: SummaryAttributeChangesets = {};

  constructor(owner: unknown, args: FlowsSectionsFinalScreenComponentSignature['Args']) {
    super(owner, args);

    this.summaryPage = this.args.summaryPage;
    this.changeset = this.args.changeset ?? this.#buildChangeset();
  }

  #buildChangeset() {
    const validations = buildValidations();
    const validator = lookupValidator(validations);
    return Changeset(this.summaryPage, validator, validations);
  }

  <template>
    <PageEditor
      @changeset={{this.changeset}}
      @defaultLocale={{@defaultLocale}}
      @flow={{@flow}}
      @globalSettingBatch={{this.currentGlobalSettingBatch}}
      @headerDescription="Show a custom message, image, or video to visitors when they finish signing in."
      @headerTitle="Custom final message"
      @isDisabled={{@isDisabled}}
      @isEditingCustomFinalMessage={{@isEditingCustomFinalMessage}}
      @showEmailMeButton={{true}}
      @summaryAttributeChangesets={{@summaryAttributeChangesets}}
      @translationEnabledLocales={{@translationEnabledLocales}}
      @translationLanguagesAvailable={{@translationLanguagesAvailable}}
    />

    <p class="text-sm mb-0">
      Learn more about
      <a href="https://help.envoy.com/custom-final-message/" target="_blank" tabindex="-1" rel="noopener">customizing
        the final screen</a>.
    </p>
  </template>
}
